// useVerifyPayment — the ONLY client caller of PATCH /api/payments/:id/verify.
// Settling a payment (match, mismatch or manual) moves it out of "pending", so any 200 refetches the
// sidebar Payments badge. 4xx/5xx and network errors change nothing and do not refetch.
import { useState } from "react";
import { usePaymentsPendingCount } from "../contexts/PaymentsPendingCountContext.js";
import { patchJson } from "./useApi.js";
import type { ManualProofInput } from "@dm-lms/shared/validations";
import type { PaymentVerification, PaymentWithProof } from "@dm-lms/shared/types";

// A code to compare with the student's, or a manual confirmation that carries proof (see verifyPaymentSchema).
export type VerifyBody = { adminCode: string } | ManualProofInput;

export function useVerifyPayment(paymentId: string, onSettled?: (payment: PaymentWithProof) => void) {
  const { refetchPendingCount } = usePaymentsPendingCount();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolves with the server's verdict on any 200, or null when the request failed (error is set).
  async function verify(body: VerifyBody): Promise<PaymentVerification | null> {
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await patchJson<PaymentVerification>(`/api/payments/${paymentId}/verify`, body);
      if (!res.ok) {
        setError(res.error);
        return null;
      }
      refetchPendingCount();
      onSettled?.(res.data.payment);
      if (!res.data.match) setError(`✗ ${res.data.message ?? "Code mismatch"}`);   // the input stays, so the admin can retry
      return res.data;
    } catch {
      setError("Unable to verify payment. Please try again.");
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }

  return { isSubmitting, error, verify };
}
